// WAN und Rechner aus dem Homelab-Protokoll (Topics `wan.lage`, `host.stand`,
// `host.stats`, `host.lage`; docs/protokoll.md › „WAN und Rechner über das
// Protokoll“) in genau die Formen, die die Seiten aus den REST-Antworten kennen:
//   - GET /v2-api/wan/summary   → `WanStatus` mit `WanRouter` je Router,
//   - GET /v2-api/cluster/stats → `ClusterStats` je Cluster mit
//     `ClusterStatsHost` je Rechner,
//   - GET /v2-api/baselines/host_stats (nur die Anomalien) → `HostAnomaly`.
//
// Rein, ohne Runen und ohne Laufzeit-Importe, damit node (Test
// lib/rechner-protokoll.test.mjs) sie laden kann. Die Stände kommen skaliert an
// (`skaliere("wan.lage", …)`: Mbit/s, Anomalie-Zahlen); Gegenstück der Rechnung
// ist der Adapter in internal/protokoll/adapter/rechner.go. Die Regeln, mit
// denen die REST-Antworten exakt wiederkehren, sind die von netz-protokoll.ts:
//   - Felder ohne `omitempty` in Go stehen immer (fehlt der Wert: ""/0/false);
//     Ausnahme die Zeiger `online` (null = unbekannt). Zeiten ohne `omitempty`
//     (`last_check`, `last_success`, `last_failure`, `updated`) tragen ohne Wert
//     die Nullzeit von Go. (`omitempty` an einem `time.Time` lässt Go nicht
//     weg -- darum steht `last_failure` immer da.)
//   - Felder mit `omitempty` stehen nur, wenn der Stand sie trägt.
//   - Zeiten sind im Stand Unix-Millisekunden und werden ISO-Text.
//   - Die Rechner stehen in `position` ihres Clusters, die Cluster in der
//     Reihenfolge von `host.lage`.

import type { WanRouter, WanStatus } from "./netzwerk/netz";
import type {
  ClusterStats,
  ClusterStatsHost,
  ClusterStatsHostStats,
  ClusterStatsHostUpdates,
  HostAufenthalt as HostAufenthaltRest,
} from "./types";
import type { HostAnomaly } from "./hosts-summary.svelte";
import type {
  HostAufenthalt,
  HostLage,
  HostStand,
  HostStats,
  HostUpdates,
  WanLage,
  WanRouter as WanRouterStand,
} from "./protokoll/schema.gen";

/** So schreibt Go `time.Time{}` als JSON. */
export const NULLZEIT = "0001-01-01T00:00:00Z";

// Wert, wenn vorhanden (nicht null/undefined), sonst Rückfall.
const s = (v: string | null | undefined): string => v ?? "";
const n = (v: number | null | undefined): number => v ?? 0;
const b = (v: boolean | null | undefined): boolean => v ?? false;
const z = <T>(v: T | null | undefined): T | null => v ?? null;

/** Setzt `o[k]`, wenn der Wert „da“ ist (omitempty: Text nicht leer, Zahl ungleich 0). */
function wenn<T extends object, K extends keyof T>(o: T, k: K, v: T[K] | null | undefined): void {
  if (v) o[k] = v as T[K];
}

function istZeit(ms: number | null | undefined): ms is number {
  return typeof ms === "number" && Number.isFinite(ms) && !Number.isNaN(new Date(ms).getTime());
}

/** Unix-Millisekunden → ISO-Text; fehlt/unlesbar → die Nullzeit der REST-Antwort. */
function zeit(ms: number | null | undefined): string {
  return istZeit(ms) ? new Date(ms).toISOString() : NULLZEIT;
}

// Ein Segment des Instanznamens: Buchstaben, Ziffern, Binde- und Unterstrich (hub.GueltigesSegment).
const SEGMENT = /^[0-9a-z_-]+$/i;

// Instanzname mit genau drei Segmenten `host.<id>.<endung>` → id (sonst undefined).
function hostIdAusInstanz(instanz: string, endung: string): string | undefined {
  const t = instanz.split(".");
  if (t.length !== 3 || t[0] !== "host" || t[2] !== endung || t[1] === "" || !SEGMENT.test(t[1])) return undefined;
  return t[1];
}

/** „host.forge.stand“ → „forge“ (die Rechner-ID). `host.lage`, `host.forge.stats` und alles andere → undefined. */
export function hostIdAusStandInstanz(instanz: string): string | undefined {
  return hostIdAusInstanz(instanz, "stand");
}

/** „host.forge.stats“ → „forge“ (die Rechner-ID). `host.lage`, `host.forge.stand` und alles andere → undefined. */
export function hostIdAusStatsInstanz(instanz: string): string | undefined {
  return hostIdAusInstanz(instanz, "stats");
}

// ── Internet ───────────────────────────────────────────────────────────

function router(r: WanRouterStand): WanRouter {
  const o: WanRouter = {
    host: s(r.host),
    quelle: s(r.quelle) as WanRouter["quelle"],
    status: s(r.status) as WanRouter["status"],
    rx_mbps: n(r.empfang),
    tx_mbps: n(r.senden),
  };
  wenn(o, "name", r.name);
  wenn(o, "cluster", r.cluster);
  wenn(o, "cluster_name", r.cluster_name);
  if (istZeit(r.seit)) o.seit = zeit(r.seit);
  if (istZeit(r.zuletzt)) o.last_seen = zeit(r.zuletzt);
  if (r.stumm) o.stumm = true;
  // Zeiger: false ist ein Wert, fehlt = unbekannt.
  if (r.wan_oben != null) o.wan_up = r.wan_oben;
  wenn(o, "ipv4", r.ipv4);
  wenn(o, "ipv6", r.ipv6);
  return o;
}

/** Das Internet-Summary aus seinem Stand, wie `GET /v2-api/wan/summary` es führt.
 *  `routers` steht immer (leer, wenn kein Router gemeldet hat). */
export function wanAusProtokoll(l: WanLage): WanStatus {
  const o: WanStatus = {
    ok: b(l.ok),
    status: s(l.status) as WanStatus["status"],
    consec_fails: n(l.fehlversuche),
    last_check: zeit(l.geprueft),
    last_success: zeit(l.erfolg),
    last_failure: zeit(l.fehlschlag),
    target: s(l.ziel),
    routers: (l.routers ?? []).map(router),
  };
  wenn(o, "last_failure_msg", l.fehler);
  if (istZeit(l.seit)) o.seit = zeit(l.seit);
  wenn(o, "ipv4", l.ipv4);
  wenn(o, "ipv6", l.ipv6);
  wenn(o, "rx_mbps", l.empfang);
  wenn(o, "tx_mbps", l.senden);
  wenn(o, "router", l.router);
  return o;
}

// ── Rechner ────────────────────────────────────────────────────────────

function updates(u: HostUpdates): ClusterStatsHostUpdates {
  return {
    pending: n(u.offen),
    deferred: n(u.zurueckgehalten),
    security_pending: n(u.sicherheit),
    reboot_required: b(u.neustart),
    uu_active: b(u.uu_aktiv),
    last_apt_update: n(u.apt_zuletzt),
    updated: zeit(u.zeit),
  };
}

function aufenthalt(a: HostAufenthalt): HostAufenthaltRest {
  const o: HostAufenthaltRest = { adresse: s(a.adresse), vom_agenten: b(a.vom_agenten) };
  wenn(o, "eingetragen", a.eingetragen);
  wenn(o, "ort", a.ort);
  wenn(o, "netz", a.netz);
  // `omitzero` in Go: ohne Zeit fehlt das Feld.
  if (istZeit(a.gemeldet)) o.gemeldet = zeit(a.gemeldet);
  return o;
}

/** Die laufenden Werte eines Rechners aus `host.<id>.stats`, wie `GET
 *  /v2-api/cluster/stats` sie in `stats` führt. `zeitMs` ist die Zeit der
 *  jüngsten Meldung der Quelle (`updated`). */
export function statsAusProtokoll(st: HostStats, zeitMs: number | undefined): ClusterStatsHostStats {
  const o: ClusterStatsHostStats = {
    cpu: z(st.cpu),
    cpu_temp: z(st.cpu_temp),
    mem_pct: z(st.mem_pct),
    uptime_s: z(st.uptime_s),
    updated: zeit(zeitMs),
  };
  if (st.power != null) {
    const p: NonNullable<ClusterStatsHostStats["power"]> = {
      throttled_hex: s(st.power.throttled_hex),
      throttled_now: n(st.power.throttled_now),
      throttled_seen: n(st.power.throttled_seen),
    };
    // Zeiger: 0 V wäre ein Wert, fehlt = nicht gemeldet.
    if (typeof st.power.volt_core === "number") p.volt_core = st.power.volt_core;
    o.power = p;
  }
  if (st.wifi != null) {
    const w: NonNullable<ClusterStatsHostStats["wifi"]> = { clients: n(st.wifi.clients) };
    const baender = st.wifi.bands ?? [];
    if (baender.length > 0) w.bands = baender.map((x) => ({ band: s(x.band), clients: n(x.clients) }));
    o.wifi = w;
  }
  return o;
}

/** Der Rechner aus seinem Stand, wie `GET /v2-api/cluster/stats` ihn in `hosts`
 *  führt. `stats` (der Stand aus `host.<id>.stats` mit der Zeit seiner jüngsten
 *  Meldung) kommt dazu, wenn er da ist -- ohne ihn fehlt `stats`. `id` fehlt im
 *  Stand nie; fehlt es doch, gilt `idInstanz`. */
export function hostAusProtokoll(
  stand: HostStand,
  stats: HostStats | undefined,
  statsZeitMs: number | undefined,
  idInstanz = "",
): ClusterStatsHost {
  const online = z(stand.online);
  const state: ClusterStatsHost["state"] = { online, offline_24h: n(stand.offline_24h) };
  if (istZeit(stand.seit)) state.status_since = zeit(stand.seit);
  const o: ClusterStatsHost = {
    id: stand.id ?? idInstanz,
    name: s(stand.name),
    sleep_ok: b(stand.schlafen_ok),
    reboot_priority: n(stand.neustart_prio),
    reboot_weekday: n(stand.neustart_tag),
    state,
    online,
  };
  wenn(o, "ip", stand.ip);
  wenn(o, "mac", stand.mac);
  wenn(o, "role", stand.rolle);
  wenn(o, "os", stand.os);
  wenn(o, "temp_threshold", stand.temp_grenze);
  if (stand.neustart_nach && stand.neustart_nach.length > 0) o.reboot_depends_on = [...stand.neustart_nach];
  wenn(o, "source", stand.quelle);
  if (stats != null) o.stats = statsAusProtokoll(stats, statsZeitMs);
  if (stand.updates != null) o.updates = updates(stand.updates);
  if (stand.noch_nicht_in_betrieb) o.noch_nicht_in_betrieb = true;
  // Zeiger: false ist ein Wert, fehlt = nie geprüft.
  if (stand.ssh_ok != null) o.ssh_reachable = stand.ssh_ok;
  wenn(o, "ssh_error", stand.ssh_fehler);
  wenn(o, "ssh_adresse", stand.ssh_adresse);
  if (stand.aufenthalt != null) o.aufenthalt = aufenthalt(stand.aufenthalt);
  return o;
}

/** Die Stelle des Rechners in seinem Cluster; fehlt sie → undefined. */
export function positionAusStand(p: { position?: number | null }): number | undefined {
  return typeof p.position === "number" && Number.isFinite(p.position) ? p.position : undefined;
}

/** Ein Rechner mit seiner Stelle: der Cluster und der Index darin. */
export type HostEintrag = ClusterStatsHost & { cluster: string; position: number };

/** Die Cluster mit ihren Rechnern, wie `GET /v2-api/cluster/stats` sie führt:
 *  die Cluster in der Reihenfolge von `lage.cluster` (auch leere), die Rechner
 *  nach `position` (bei Gleichstand nach ID). Rechner eines Clusters, den die
 *  Lage nicht kennt, fehlen -- die Lage kommt im selben Bündel. Ohne Lage →
 *  keine Cluster. */
export function clusterStatsAus(lage: HostLage | undefined, hosts: ReadonlyMap<string, HostEintrag>): ClusterStats[] {
  const out: ClusterStats[] = [];
  for (const c of lage?.cluster ?? []) {
    const id = s(c.id);
    const eigene = [...hosts.values()].filter((h) => h.cluster === id);
    eigene.sort((a, c2) => a.position - c2.position || (a.id < c2.id ? -1 : a.id > c2.id ? 1 : 0));
    out.push({
      id,
      name: s(c.name),
      hosts: eigene.map((h) => {
        const { cluster: _c, position: _p, ...rest } = h;
        return rest;
      }),
    });
  }
  return out;
}

// ── Lage ───────────────────────────────────────────────────────────────

/** Die Anomalien gegen die Baseline (wie sie `GET /v2-api/baselines/host_stats`
 *  nennt), schärfste zuerst -- in der Reihenfolge des Standes. */
export function anomalienAusProtokoll(lage: HostLage | undefined): HostAnomaly[] {
  return (lage?.anomalien ?? []).map((a) => ({
    host_id: s(a.host),
    metric: s(a.metrik),
    current: n(a.aktuell),
    avg: n(a.mittel),
    z_score: n(a.z),
  }));
}

/** Die Rechner, deren Offline-Sein erwartet ist (`expected_offline_ok`). */
export function offlineOkAusProtokoll(lage: HostLage | undefined): string[] {
  return [...(lage?.offline_ok ?? [])];
}

/** Der Rechner für den Verlauf, wenn keine Anomalie da ist; leer → keiner. */
export function verlaufHostAusProtokoll(lage: HostLage | undefined): string {
  return s(lage?.verlauf_host);
}

/** Die Cluster der Lage als (id, name)-Liste. */
export function clusterListeAusProtokoll(lage: HostLage | undefined): { id: string; name: string }[] {
  return (lage?.cluster ?? []).map((c) => ({ id: s(c.id), name: s(c.name) }));
}
